import { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { TextField } from '@mui/material';
import { Button } from '@mui/material';
import { Popover, PopoverProps } from '@mui/material';
import { AdapterDayjs as AdapterDayjsPro } from '@mui/x-date-pickers-pro/AdapterDayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
    LocalizationProvider as LocalizationProProvider,
    SingleInputDateRangeFieldProps,
    DateRangeCalendar,
    DateRangeCalendarProps,
    DateRangePicker,
    SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker, PickersShortcutsItem } from '@mui/x-date-pickers';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import { isSameDay } from 'date-fns';

import { adjustDateRangeInPast } from '../pages/insights/utils';
import Row from './Row';
import { ArrowDropDown } from '@mui/icons-material';

dayjs.extend(quarterOfYear);

interface StaticRange {
    label: string;
    range: () => DateRange<Dayjs>;
    isSelected: (range: DateRange<Dayjs>) => boolean;
}

const getStaticRange = (label: string, startDate: Date, endDate: Date): StaticRange => {
    return {
        label: label,
        range() {
            return [dayjs(startDate), dayjs(endDate)];
        },
        isSelected: (range: DateRange<Dayjs>) => {
            if (range[0] && range[1]) {
                return isSameDay(range[0].toDate(), startDate) && isSameDay(range[1].toDate(), endDate);
            } else {
                return false;
            }
        },
    };
};

const getWeeklyStaticRange = (label: string, startDate: Date, weeksInPast: number): StaticRange => {
    const dateRange = adjustDateRangeInPast({
        startDate: startDate,
        weeksInPast: weeksInPast,
    });

    return {
        label: label,
        range() {
            return [dayjs(dateRange.startDate), dayjs(dateRange.endDate)];
        },
        isSelected: (range: DateRange<Dayjs>) => {
            if (range[0] && range[1]) {
                return (
                    isSameDay(range[0].toDate(), dateRange.startDate) && isSameDay(range[1].toDate(), dateRange.endDate)
                );
            } else {
                return false;
            }
        },
    };
};

const getDefaultDateRanges = (options?: { maxDate?: Date }): PickersShortcutsItem<DateRange<Dayjs>>[] => {
    const { maxDate: defaultMaxDate } = options || {};

    const today = dayjs().startOf('day');
    const maxDate = defaultMaxDate ? dayjs(defaultMaxDate) : today.endOf('day');

    return [
        {
            label: 'This Month',
            getValue: () => {
                return [today.startOf('month'), maxDate];
            },
        },
        {
            label: 'This Quarter',
            getValue: () => {
                return [today.startOf('quarter'), maxDate];
            },
        },
        {
            label: 'Last Month',
            getValue: () => {
                return [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')];
            },
        },
        {
            label: 'Last Quarter',
            getValue: () => {
                const lastQuarter = today.quarter() === 1 ? 4 : today.quarter() - 1;
                return [today.quarter(lastQuarter).startOf('quarter'), today.quarter(lastQuarter).endOf('quarter')];
            },
        },
        {
            label: 'Last 30 Days',
            getValue: () => {
                return [today.subtract(30, 'days'), maxDate];
            },
        },
        {
            label: 'Last 60 Days',
            getValue: () => {
                return [today.subtract(60, 'days'), maxDate];
            },
        },
        {
            label: 'Last 90 Days',
            getValue: () => {
                return [today.subtract(90, 'days'), maxDate];
            },
        },
        {
            label: 'Last 120 Days',
            getValue: () => {
                return [today.subtract(120, 'days'), maxDate];
            },
        },
        {
            label: 'Last 150 Days',
            getValue: () => {
                return [today.subtract(150, 'days'), maxDate];
            },
        },
        {
            label: 'Last 180 Days',
            getValue: () => {
                return [today.subtract(180, 'days'), maxDate];
            },
        },
        { label: 'Reset', getValue: () => [null, null] },
    ];
};

interface CustomDateRangePickerProps {
    label: string;
    value: DateRange<Dayjs>;
    onChange: (newRange: DateRange<Dayjs>) => void;
    onSelect?: (newRange: DateRange<Dayjs>) => void;
    onClose?: () => void;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    disableFuture?: boolean;
    disablePast?: boolean;
    ranges?: PickersShortcutsItem<DateRange<Dayjs>>[];
    open?: boolean;
    SingleInputDateRangeFieldProps?: SingleInputDateRangeFieldProps<Dayjs>;
    DateRangeCalendarProps?: DateRangeCalendarProps<Dayjs>;
    PopoverProps?: PopoverProps;
    anchorOrigin?: PopoverProps['anchorOrigin'];
    transformOrigin?: PopoverProps['transformOrigin'];
    showToolbar?: boolean;
    toolbarText?: string;
    shouldDisableDate?: (date: Dayjs) => boolean;
    width?: number | string;
    fieldClearable?: boolean;
}

const CustomDateRangePicker = ({ width = 248, ...props }: CustomDateRangePickerProps) => {
    const [open, setOpen] = useState<boolean>(props.open ?? false);
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(props.value);
    const $anchorEl = useRef<any>(null);

    useEffect(() => {
        setDateRange(props.value);
    }, [props.value]);

    // const triggerOpenPopover = (): void => {
    //     setOpen(true);
    // };

    const triggerClosePopover = (): void => {
        setOpen(false);

        if (props.onClose) {
            props.onClose();
        }
    };

    const resetDateRange = (): void => {
        setDateRange(props.value);
    };

    const handleClosePopover = (): void => {
        triggerClosePopover();
    };

    const handleDateRangeChange = (newRange: DateRange<Dayjs>): void => {
        setDateRange(newRange);
        props.onChange(newRange);
        // console.log(ctx.shortcut?.id);
        if (props.onSelect) {
            props.onSelect(newRange);
        }
    };

    const handleStartDateChange = (newStartDate: Date | null): void => {
        // handleDateRangeChange([dayjs(newStartDate), dateRange[1]]);
    };

    const handleEndDateChange = (newEndDate: Date | null): void => {
        // handleDateRangeChange([dateRange[0], dayjs(newEndDate)]);
    };

    const handleClear = (): void => {
        handleDateRangeChange([null, null]);
    };

    const handleDeny = (): void => {
        resetDateRange();

        triggerClosePopover();
    };

    const handleConfirm = (): void => {
        props.onChange(dateRange);

        triggerClosePopover();
    };

    function CustomCalendarHeader() {
        if (!props.showToolbar) {
            return <></>;
        }
        return <div style={{ margin: 8, width: 300 }}>{props.toolbarText}</div>;
    }

    return (
        <div>
            <LocalizationProProvider dateAdapter={AdapterDayjsPro}>
                <DateRangePicker
                    label={props.label}
                    value={props.value}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    shouldDisableDate={props.shouldDisableDate}
                    slots={{
                        field: SingleInputDateRangeField,
                        toolbar: CustomCalendarHeader,
                    }}
                    onChange={handleDateRangeChange}
                    disableFuture={props.disableFuture}
                    slotProps={{
                        shortcuts: {
                            items: props.ranges,
                        },
                        textField: { InputProps: { endAdornment: <ArrowDropDown /> }, size: 'small' },
                        field: {
                            clearable: props.fieldClearable,
                        },
                    }}
                    sx={{ width: width }}
                    // onFocus={handleFocusSingleInput}
                    // onBlur={handleFocusOutSingleInput}
                />
            </LocalizationProProvider>

            <Popover
                open={open}
                {...props.PopoverProps}
                anchorOrigin={
                    props.anchorOrigin ?? {
                        vertical: 'bottom',
                        horizontal: 'right',
                    }
                }
                transformOrigin={
                    props.transformOrigin ?? {
                        vertical: 'top',
                        horizontal: 'right',
                    }
                }
                anchorEl={$anchorEl.current}
                onClose={handleClosePopover}
            >
                <Row gap={0}>
                    <Box>
                        <Box sx={{ px: 3, py: 2 }}>
                            <Row gap={2} alignItems="center">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        minDate={props?.minDate?.toDate()}
                                        maxDate={props?.maxDate?.toDate()}
                                        value={dateRange[0] ? dateRange[0].toDate() : null}
                                        onChange={handleStartDateChange}
                                        slots={{
                                            textField: (props) => (
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ width: 184 }}
                                                    {...props}
                                                />
                                            ),
                                        }}
                                        label="Start Date"
                                        disableOpenPicker={true}
                                        disableFuture={props.disableFuture}
                                        disablePast={props.disablePast}
                                    />

                                    <Typography variant="body1">—</Typography>

                                    <DatePicker
                                        minDate={props?.minDate?.toDate()}
                                        maxDate={props?.maxDate?.toDate()}
                                        value={dateRange[1] ? dateRange[1].toDate() : null}
                                        onChange={handleEndDateChange}
                                        slots={{
                                            textField: (props) => (
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ width: 184 }}
                                                    {...props}
                                                />
                                            ),
                                        }}
                                        label="End Date"
                                        disableOpenPicker={true}
                                        disableFuture={props.disableFuture}
                                        disablePast={props.disablePast}
                                    />
                                </LocalizationProvider>
                            </Row>
                        </Box>

                        <Divider />

                        <LocalizationProProvider dateAdapter={AdapterDayjsPro}>
                            <DateRangeCalendar
                                disableFuture={props.disableFuture}
                                disablePast={props.disablePast}
                                {...props.DateRangeCalendarProps}
                                minDate={props.minDate}
                                maxDate={props.maxDate}
                                value={dateRange}
                                onChange={handleDateRangeChange}
                            />
                        </LocalizationProProvider>

                        <Divider />

                        <Box sx={{ px: 3, py: 2 }}>
                            <Row gap={1} justifyContent="flex-end">
                                <Button color="primary" variant="outlined" onClick={handleClear}>
                                    Clear
                                </Button>

                                <Button color="primary" variant="outlined" onClick={handleDeny}>
                                    Cancel
                                </Button>

                                <Button color="primary" variant="contained" onClick={handleConfirm}>
                                    Apply
                                </Button>
                            </Row>
                        </Box>
                    </Box>
                </Row>
            </Popover>
        </div>
    );
};

export type { StaticRange };
export type { CustomDateRangePickerProps };
export { getStaticRange, getWeeklyStaticRange, getDefaultDateRanges };
export default CustomDateRangePicker;
